import React from 'react';
import styled from 'styled-components';
import { TFunction, useTranslation } from 'react-i18next';
import { push as Push } from 'redux-first-history';
import { convertToLocalTime } from '@dev/ee-tools';
import { ContentLoader, DividedBox, Panel, TertiaryButton, Typography } from '@dev/ee-components';
import { isEmpty } from 'lodash-es';
import { JourneyStatus } from '@dev/ee-api/ee-api-models/journey-status';
import { useDispatch, useSelector } from 'react-redux';
import { AutomationContactType, JourneySummary } from '@dev/ee-api/ee-api-models';
import { useParams } from 'react-router-dom';

import useMobile from '@/services/hooks/useMobile';
import { formatNumber } from '@/services/tools/formatNumber';
import { ApplicationState } from '@/store';
import { AutomationPaths } from '@/services/dictionaries/Paths';
import { StatusBadge } from '@/assets/StatusBadge';

import { StyledProps } from '../AutomationReportInterface';

const BasicInfoWrapper = styled.div<StyledProps>`
    display: ${({ isMobile }) => (isMobile ? 'flex' : 'grid')};
    flex-direction: ${({ isMobile }) => (isMobile ? 'column' : '')};
    column-gap: 30px;
    grid-template-columns: ${({ isMobile }) => (isMobile ? '' : '1fr 1fr 1fr')};
    min-height: 160px;
    .GridCart:first-child {
        margin-top: 0px;
    }
    .GridCart {
        margin-top: ${({ isMobile }) => (isMobile ? 24 : '')}px;
        .view-contacts-link {
            margin-top: 6px;
            margin-bottom: ${({ isMobile }) => (isMobile ? 0 : 32)}px;
        }
    }
    .GridCart-Header {
        height: 100%;
        .headerTextWrapper {
            height: inherit;
        }
        .MuiTypography-body1 {
            height: inherit;
            .MuiBox-root {
                height: inherit;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                align-items: flex-start;
            }
        }
    }
`;
const DividedBoxWrapper = styled.div`
    padding-top: 32px;
    padding-bottom: 32px;
    div {
        min-height: 100%;
    }
`;

const ContentLoaderWrapper = styled.div`
    margin-right: 16px;
    min-width: 45px;
    flex: 1;
`;

const getStatusBadge = (status: JourneyStatus): JSX.Element => <StatusBadge value={status} status={status} />;

const getBasicInfoRows = (
    singleAutomation: JourneySummary,
    loadingSingleAutomation: boolean,
    t: TFunction<'translation'>
) => {
    let label = '';
    let date = '';

    if (singleAutomation?.Status !== JourneyStatus.Active) {
        label = t('created');
        date = singleAutomation?.DateCreated;
    } else {
        label = t('published');
        date = singleAutomation?.DatePublished;
    }

    return [
        [
            {
                label: (
                    <div style={{ marginRight: '32px', width: '85px' }}>
                        <Typography variant="body2">{t('status')}</Typography>
                    </div>
                ),
                value:
                    !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                        <Typography variant="h4">{getStatusBadge(singleAutomation?.Status)}</Typography>
                    ) : (
                        <ContentLoaderWrapper>
                            <ContentLoader mode="row" height={16} />
                        </ContentLoaderWrapper>
                    ),
            },
            {
                label: (
                    <div style={{ marginRight: '32px', width: '85px' }}>
                        <Typography variant="body2">{label}</Typography>
                    </div>
                ),
                value:
                    !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                        <Typography variant="h4">{convertToLocalTime(date)}</Typography>
                    ) : (
                        <ContentLoaderWrapper>
                            <ContentLoader mode="row" height={16} />
                        </ContentLoaderWrapper>
                    ),
            },
        ],
    ];
};

const PanelContentLoader = (): JSX.Element => (
    <ContentLoaderWrapper>
        <ContentLoader mode="row" height={16} />
    </ContentLoaderWrapper>
);

// eslint-disable-next-line sonarjs/cognitive-complexity
export const BasicInfo = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const urlParams: { name: string } = useParams();

    const { singleAutomation, loadingSingleAutomation } = useSelector((state: ApplicationState) => state?.automations);

    return (
        <>
            <BasicInfoWrapper isMobile={isMobile}>
                <Panel
                    description={
                        <>
                            <>
                                {!loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                                    <Typography variant="h1">
                                        {singleAutomation?.TotalContactsCount
                                            ? formatNumber(singleAutomation?.TotalContactsCount)
                                            : 0}
                                    </Typography>
                                ) : (
                                    <PanelContentLoader />
                                )}
                                <Typography className="view-contacts-link" variant="h3">
                                    {t('totalrecipients')}
                                </Typography>
                            </>
                            {!isMobile && (
                                <TertiaryButton
                                    text={t('viewcontacts')}
                                    disabled={singleAutomation?.TotalContactsCount === 0}
                                    onClick={(): void => {
                                        dispatch(
                                            Push(
                                                AutomationPaths.ViewContacts.replace(':name', urlParams.name).replace(
                                                    ':recipientsType',
                                                    AutomationContactType.All
                                                )
                                            )
                                        );
                                    }}
                                />
                            )}
                        </>
                    }
                    loading={!singleAutomation}
                    headerPadding="24px 32px"
                    isMobile={isMobile}
                />

                <Panel
                    description={
                        <>
                            <>
                                {!loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                                    <Typography variant="h1">
                                        {singleAutomation?.FinishedContactsCount
                                            ? formatNumber(singleAutomation?.FinishedContactsCount)
                                            : 0}
                                    </Typography>
                                ) : (
                                    <PanelContentLoader />
                                )}
                                <Typography className="view-contacts-link" variant="h3">
                                    {t('recipientshavecompletedthisautomation')}
                                </Typography>
                            </>
                            {!isMobile && (
                                <TertiaryButton
                                    text={t('viewcontacts')}
                                    disabled={singleAutomation?.FinishedContactsCount === 0}
                                    onClick={(): void => {
                                        dispatch(
                                            Push(
                                                AutomationPaths.ViewContacts.replace(':name', urlParams.name).replace(
                                                    ':recipientsType',
                                                    AutomationContactType.Completed
                                                )
                                            )
                                        );
                                    }}
                                />
                            )}
                        </>
                    }
                    loading={!singleAutomation}
                    headerPadding="24px 32px"
                />

                <Panel
                    description={
                        <>
                            <>
                                {!loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                                    <Typography variant="h1">
                                        {formatNumber(
                                            (singleAutomation?.TotalContactsCount || 0) -
                                                (singleAutomation?.FinishedContactsCount || 0)
                                        )}
                                    </Typography>
                                ) : (
                                    <PanelContentLoader />
                                )}
                                <Typography className="view-contacts-link" variant="h3">
                                    {t('recipientsarecurrentlyinthisautomation')}
                                </Typography>
                            </>
                            {!isMobile && (
                                <TertiaryButton
                                    text={t('viewcontacts')}
                                    disabled={
                                        (singleAutomation?.TotalContactsCount || 0) -
                                            (singleAutomation?.FinishedContactsCount || 0) ===
                                        0
                                    }
                                    onClick={(): void => {
                                        dispatch(
                                            Push(
                                                AutomationPaths.ViewContacts.replace(':name', urlParams.name).replace(
                                                    ':recipientsType',
                                                    AutomationContactType.InProgress
                                                )
                                            )
                                        );
                                    }}
                                />
                            )}
                        </>
                    }
                    loading={!singleAutomation}
                    headerPadding="24px 32px"
                />
            </BasicInfoWrapper>

            <DividedBoxWrapper>
                <DividedBox
                    flexTextAlign="flex-start"
                    rows={getBasicInfoRows(singleAutomation, loadingSingleAutomation, t)}
                />
            </DividedBoxWrapper>
        </>
    );
};
